/* Presentation only. The official Forms block owns loading, input state and submission. */
const OWNERS = new WeakMap();
const FORM_HOSTS = ['form-embed', 'shopify-forms-embed'];
const HOST_SELECTOR = FORM_HOSTS.join(', ');
const findHost = (root) => root.matches(HOST_SELECTOR) ? root : root.querySelector(HOST_SELECTOR);
const TOKENS = [
  'width', 'width-mobile', 'max-width', 'field-gap', 'field-padding', 'button-gap',
  'space-above', 'space-below', 'border-width', 'border-radius',
  ...['input', 'button', 'heading'].flatMap((kind) =>
    ['family', 'size', 'style', 'weight', 'line-height', 'letter-spacing', 'case'].map((name) => `${kind}-${name}`)
  ),
];
// Calling prefixes from the native Shopify Forms country list. No extra request or phone library.
const CALLING_CODES = Object.freeze({"AF":"93","AX":"358","AL":"355","DZ":"213","AD":"376","AO":"244","AI":"1","AG":"1","AR":"54","AM":"374","AW":"297","AC":"247","AU":"61","AT":"43","AZ":"994","BS":"1","BH":"973","BD":"880","BB":"1","BY":"375","BE":"32","BZ":"501","BJ":"229","BM":"1","BT":"975","BO":"591","BA":"387","BW":"267","BV":"47","BR":"55","IO":"246","BN":"673","BG":"359","BF":"226","BI":"257","KH":"855","CA":"1","CV":"238","BQ":"599","KY":"1","CF":"236","TD":"235","CL":"56","CN":"86","CX":"61","CC":"891","CO":"57","KM":"269","CG":"242","CD":"243","CK":"682","CR":"506","HR":"385","CU":"53","CW":"599","CY":"357","CZ":"420","CI":"225","DK":"45","DJ":"253","DM":"1","DO":"1","EC":"593","EG":"20","SV":"503","GQ":"240","ER":"291","EE":"372","SZ":"268","ET":"251","FK":"500","FO":"298","FJ":"679","FI":"358","FR":"33","GF":"594","PF":"689","TF":"262","GA":"241","GM":"220","GE":"995","DE":"49","GH":"233","GI":"350","GR":"30","GL":"299","GD":"1","GP":"590","GT":"502","GG":"44","GN":"224","GW":"245","GY":"592","HT":"509","HM":"0","VA":"39","HN":"504","HK":"852","HU":"36","IS":"354","IN":"91","ID":"62","IR":"98","IQ":"964","IE":"353","IM":"44","IL":"972","IT":"39","JM":"1","JP":"81","JE":"44","JO":"962","KZ":"7","KE":"254","KI":"686","KP":"850","XK":"383","KW":"965","KG":"996","LA":"856","LV":"371","LB":"961","LS":"266","LR":"231","LY":"218","LI":"423","LT":"370","LU":"352","MO":"853","MG":"261","MW":"265","MY":"60","MV":"960","ML":"223","MT":"356","MQ":"596","MR":"222","MU":"230","YT":"262","MX":"52","MD":"373","MC":"377","MN":"976","ME":"382","MS":"1","MA":"212","MZ":"258","MM":"95","NA":"264","NR":"674","NP":"977","NL":"31","AN":"599","NC":"687","NZ":"64","NI":"505","NE":"227","NG":"234","NU":"683","NF":"672","MK":"389","NO":"47","OM":"968","PK":"92","PS":"970","PA":"507","PG":"675","PY":"595","PE":"51","PH":"63","PN":"64","PL":"48","PT":"351","QA":"974","CM":"237","RE":"262","RO":"40","RU":"7","RW":"250","BL":"590","SH":"290","KN":"1","LC":"1","MF":"590","PM":"508","WS":"685","SM":"378","ST":"239","SA":"966","SN":"221","RS":"381","SC":"248","SL":"232","SG":"65","SX":"1","SK":"421","SI":"386","SB":"677","SO":"252","ZA":"27","GS":"500","KR":"82","SS":"211","ES":"34","LK":"94","VC":"1","SD":"249","SR":"597","SJ":"47","SE":"46","CH":"41","SY":"963","TW":"886","TJ":"992","TZ":"255","TH":"66","TL":"670","TG":"228","TK":"690","TO":"676","TT":"1","TA":"2908","TN":"216","TR":"90","TM":"993","TC":"1","TV":"688","UG":"256","UA":"380","AE":"971","GB":"44","US":"1","UM":"1","UY":"598","UZ":"998","VU":"678","VE":"58","VN":"84","VG":"1","WF":"681","EH":"212","YE":"967","ZM":"260","ZW":"263"});
const INPUTS = 'input:not([type="checkbox"]):not([type="radio"]):not([type="hidden"]):not([type="submit"]):not([type="button"]):not([type="file"])';

class ParaShopifyFormStyle extends HTMLElement {
  connectedCallback() {
    if (this.controller && !this.controller.signal.aborted) return;
    this.controller = new AbortController();
    this.records = new Map();
    this.source = this.closest('.custom-signup-block');
    this.id = this.dataset.formId?.trim();
    if (!this.source || !/^\d+$/.test(this.id || '')) {
      this.status('Enter a numeric Shopify Form ID. Keep its official Forms block on this page.');
      return;
    }
    this.selector = `[data-forms-id="forms-root-${this.id}"]`;
    this.css = this.querySelector('template[data-forms-style]')?.content.querySelector('style')?.textContent;
    if (!this.css) return;

    this.observer = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => [...mutation.addedNodes, ...mutation.removedNodes].some((node) =>
        node.nodeType === Node.ELEMENT_NODE && (
          node.matches(`${this.selector}, ${HOST_SELECTOR}`) || node.querySelector(`${this.selector}, ${HOST_SELECTOR}`)
        )
      ))) this.schedule();
    });
    this.observer.observe(document.body, { childList: true, subtree: true });
    const { signal } = this.controller;
    window.addEventListener('resize', () => this.schedule(), { passive: true, signal });
    document.addEventListener('shopify:section:load', () => this.schedule(), { signal });
    document.addEventListener('shopify:block:select', () => this.schedule(), { signal });
    document.fonts?.addEventListener('loadingdone', () => {
      for (const record of this.records.values()) record.prefixWidths.clear();
      this.schedule();
    }, { signal });
    for (const tag of FORM_HOSTS) {
      customElements.whenDefined(tag).then(() => {
        if (!signal.aborted) this.schedule();
      });
    }
    this.refresh();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    cancelAnimationFrame(this.frame);
    this.frame = null;
    for (const record of this.records?.values() || []) this.release(record);
    this.records?.clear();
  }

  schedule() {
    if (this.frame != null || !this.isConnected) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      if (this.isConnected) this.refresh();
    });
  }

  status(text) {
    const output = this.querySelector('[data-forms-status]');
    if (output && output.textContent !== text) output.textContent = text;
  }

  setAttributeFor(record, element, name, value) {
    let attributes = record.attributes.get(element);
    if (!attributes) record.attributes.set(element, attributes = new Map());
    if (!attributes.has(name)) attributes.set(name, { before: element.getAttribute(name), applied: value });
    attributes.get(name).applied = value;
    if (element.getAttribute(name) !== value) element.setAttribute(name, value);
  }

  setPropertyFor(record, element, name, value) {
    let saved = record.properties.get(element);
    if (!saved) record.properties.set(element, saved = new Map());
    if (!saved.has(name)) saved.set(name, {
      before: element.style.getPropertyValue(name), priority: element.style.getPropertyPriority(name), applied: value,
    });
    saved.get(name).applied = value;
    if (element.style.getPropertyValue(name) !== value) element.style.setProperty(name, value);
  }

  refresh() {
    const roots = new Set(document.querySelectorAll(this.selector));
    for (const [root, record] of this.records) {
      if (!roots.has(root) || findHost(root) !== record.host) {
        this.release(record);
        this.records.delete(root);
      }
    }
    const computed = getComputedStyle(this.source);
    const read = (name, fallback = '') => computed.getPropertyValue(name).trim() || fallback;
    const properties = Object.fromEntries(TOKENS.map((name) => [`--signup-${name}`, read(`--signup-${name}`)]));
    const margins = (alignment) => alignment === 'left' ? ['0px', 'auto'] : alignment === 'right' ? ['auto', '0px'] : ['auto', 'auto'];
    const [start, end] = margins(this.dataset.alignment);
    const [mobileStart, mobileEnd] = margins(this.dataset.mobileAlignment);
    const secondary = this.dataset.button === 'button-secondary';
    const link = this.dataset.button === 'button-unstyled';
    const prefix = secondary ? '--color-secondary-button-' : '--color-primary-button-';
    Object.assign(properties, {
      '--signup-margin-start': start, '--signup-margin-end': end,
      '--signup-mobile-margin-start': mobileStart, '--signup-mobile-margin-end': mobileEnd,
      '--signup-text-color': read('--color-input-text', '#202020'),
      '--signup-background': read('--color-background', '#ffffff'),
      '--signup-field-background': this.dataset.border === 'underline' ? 'transparent' : read('--color-input-background', '#ffffff'),
      '--signup-border-color': read('--color-input-border', '#cccccc'),
      '--signup-field-border': this.dataset.border === 'none' ? '0px' : read('--signup-border-width', '1px'),
      '--signup-field-sides': this.dataset.border === 'underline' || this.dataset.border === 'none' ? '0px' : read('--signup-border-width', '1px'),
      '--signup-field-radius': this.dataset.border === 'underline' ? '0px' : read('--signup-border-radius', '0px'),
      '--signup-field-inline-padding': this.dataset.border === 'underline' ? '0px' : '16px',
      '--signup-submit-background': link ? 'transparent' : read(`${prefix}background`, '#202020'),
      '--signup-submit-color': read(link ? '--color-input-text' : `${prefix}text`, '#202020'),
      '--signup-submit-border': link ? 'transparent' : read(`${prefix}border`, 'transparent'),
      '--signup-submit-radius': read(secondary ? '--style-border-radius-buttons-secondary' : '--style-border-radius-buttons-primary', '0px'),
      '--signup-submit-border-width': link ? '0px' : read(secondary ? '--style-border-width-buttons-secondary' : '--style-border-width-buttons-primary', '1px'),
      '--signup-submit-hover-background': link ? 'transparent' : read(`${prefix}hover-background`, read(`${prefix}background`, '#202020')),
      '--signup-submit-hover-color': read(link ? '--color-input-text' : `${prefix}hover-text`, '#202020'),
    });

    for (const root of roots) {
      if (OWNERS.has(root) && OWNERS.get(root) !== this) continue;
      let record = this.records.get(root);
      if (!record) {
        const host = findHost(root);
        if (!host?.shadowRoot) continue;
        const wrapper = root.closest('.shopify-block') || root;
        record = { root, host, wrapper, attributes: new Map(), properties: new Map(), placeholders: new Map(), countries: new WeakMap(), prefixWidths: new Map(), countryTimers: new Set(), events: new AbortController() };
        record.style = document.createElement('style');
        record.style.dataset.paraSignupStyle = this.id;
        record.style.textContent = this.css;
        host.shadowRoot.append(record.style);
        const scheduleRecord = () => {
          if (!this.isConnected || record.frame != null) return;
          record.frame = requestAnimationFrame(() => {
            record.frame = null;
            if (!record.host.shadowRoot.contains(record.style)) record.host.shadowRoot.append(record.style);
            this.decorateLabels(record);
            this.decorateCountries(record);
          });
        };
        record.schedule = scheduleRecord;
        record.observer = new MutationObserver(scheduleRecord);
        record.observer.observe(host.shadowRoot, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['src', 'alt'] });
        const phoneEvent = (event) => {
          if (event.target.matches?.('input[type="tel"]')) scheduleRecord();
        };
        for (const name of ['input', 'change', 'paste', 'focusin', 'click', 'keyup', 'select']) {
          host.shadowRoot.addEventListener(name, phoneEvent, { signal: record.events.signal });
        }
        // Browsers can scroll negative text indentation back into view on focus/Home.
        host.shadowRoot.addEventListener('scroll', (event) => {
          const input = event.target;
          if (input.matches?.('input[data-para-phone-prefix]') && input.scrollLeft < 0) input.scrollLeft = 0;
        }, { capture: true, passive: true, signal: record.events.signal });
        this.records.set(root, record);
        OWNERS.set(root, this);
      }
      this.setAttributeFor(record, record.wrapper, 'data-para-signup-layout', this.id);
      this.setAttributeFor(record, root, 'data-para-signup-root', this.id);
      this.setAttributeFor(record, record.host, 'data-para-signup-border', this.dataset.border || 'all');
      this.setAttributeFor(record, record.host, 'data-para-signup-button', this.dataset.button || 'button');
      // Put tokens on the host as well: Forms may set its own theme colors on the container.
      for (const element of new Set([record.wrapper, record.host])) {
        let saved = record.properties.get(element);
        if (!saved) record.properties.set(element, saved = new Map());
        for (const [name, value] of Object.entries(properties)) {
          if (!saved.has(name)) saved.set(name, {
            before: element.style.getPropertyValue(name), priority: element.style.getPropertyPriority(name), applied: value,
          });
          saved.get(name).applied = value;
          if (element.style.getPropertyValue(name) !== value) element.style.setProperty(name, value);
        }
      }
      this.decorateLabels(record);
      this.decorateCountries(record);
    }
    this.status(this.records.size
      ? `Style linked to Shopify Form ${this.id}. Edit fields, consent and text in Apps > Forms.`
      : `Waiting for Shopify Form ${this.id}. Keep its official inline Forms block enabled on this page.`);
  }

  decorateLabels(record) {
    if (this.dataset.labelStyle !== 'placeholder') return;
    const shadow = record.host.shadowRoot;
    for (const input of shadow.querySelectorAll(`form ${INPUTS}`)) {
      // Only replace the visual floating-label treatment, never checkbox/consent labels.
      const wrapper = input.parentElement;
      const label = wrapper?.querySelector('label[class*="formInputFieldLabel"]');
      if (!label) continue;
      let saved = record.placeholders.get(input);
      if (!saved) record.placeholders.set(input, saved = { before: input.getAttribute('placeholder'), applied: null });
      const placeholder = saved.before || label.textContent.trim();
      if (input.getAttribute('placeholder') !== placeholder) input.setAttribute('placeholder', placeholder);
      saved.applied = placeholder;
      this.setAttributeFor(record, input, 'data-para-signup-placeholder', 'true');
      this.setAttributeFor(record, label, 'data-para-signup-label', 'true');
    }
  }

  decorateCountries(record) {
    for (const field of record.host.shadowRoot.querySelectorAll('[class*="formPhoneInputContainer"]')) {
      // Newer Forms adds aria-controls only while the menu is open.
      const button = field.querySelector('.phone-country-selector button, button[aria-expanded]');
      const text = button?.querySelector('[id="country-selector-value"], [class*="selectToggleText"]');
      const flag = text?.querySelector('img');
      if (!flag) continue;
      const countryName = flag.alt.trim();
      const iso = flag.getAttribute('src')?.match(/\/flags\/([a-z]{2})\.svg(?:[?#]|$)/i)?.[1]?.toUpperCase();
      const selected = field.querySelector('[role="option"][aria-selected="true"]');
      const selectedIso = selected?.id.match(/^select-country-([a-z]{2})$/i)?.[1]?.toUpperCase();
      const previous = record.countries.get(flag);
      // Retain a known prefix if Shopify substitutes a fallback image for the same country.
      const code = CALLING_CODES[iso] || CALLING_CODES[selectedIso]
        || (previous?.name === countryName ? previous.code : null);
      if (!code) {
        this.decoratePhonePrefix(record, field, null);
        for (const [element, name] of [[text, 'data-para-signup-dial-code'], [button, 'data-para-signup-country'], [button, 'aria-label']]) {
          const saved = record.attributes.get(element)?.get(name);
          if (!saved) continue;
          if (element.getAttribute(name) === saved.applied) {
            if (saved.before === null) element.removeAttribute(name);
            else element.setAttribute(name, saved.before);
          }
          record.attributes.get(element).delete(name);
        }
        continue;
      }
      if (previous?.code !== code) {
        // Forms updates input.value in a delayed effect after changing the country flag.
        // Two bounded follow-ups cover that update; no ongoing polling or value interception.
        for (const timer of record.countryTimers) clearTimeout(timer);
        record.countryTimers.clear();
        for (const delay of [100, 300]) {
          const timer = setTimeout(() => {
            record.countryTimers.delete(timer);
            record.schedule();
          }, delay);
          record.countryTimers.add(timer);
        }
      }
      record.countries.set(flag, { name: countryName, code });
      // CSS draws the prefix without changing native children, input values or event handlers.
      this.setAttributeFor(record, text, 'data-para-signup-dial-code', `+${code}`);
      this.setAttributeFor(record, button, 'data-para-signup-country', 'code');
      // Recent Forms supplies a full accessible label; keep its native wording and updates.
      if (!button.hasAttribute('aria-label') || record.attributes.get(button)?.has('aria-label')) {
        this.setAttributeFor(record, button, 'aria-label', `${countryName} +${code}`.trim());
      }
      this.decoratePhonePrefix(record, field, code);
    }
  }

  decoratePhonePrefix(record, field, code) {
    const input = field.querySelector('input[type="tel"]');
    if (!input) return;
    const start = code ? `+${code}` : '';
    const prefix = start && input.value.startsWith(start)
      ? start + (input.value.slice(start.length).match(/^\s*/)?.[0] || '') : '';
    let width = 0;
    if (prefix) {
      const computed = getComputedStyle(input);
      const names = ['font', 'letter-spacing', 'word-spacing', 'font-kerning', 'font-feature-settings', 'font-variation-settings', 'font-variant-numeric'];
      const styles = names.map((name) => [name, computed.getPropertyValue(name)]);
      const key = JSON.stringify([prefix, styles]);
      width = record.prefixWidths.get(key);
      if (width === undefined) {
        if (!record.ruler) {
          record.ruler = document.createElement('span');
          record.ruler.setAttribute('aria-hidden', 'true');
          record.ruler.style.cssText = 'position:fixed;visibility:hidden;pointer-events:none;white-space:pre;inset:0 auto auto 0;';
          record.host.shadowRoot.append(record.ruler);
        }
        for (const [name, value] of styles) record.ruler.style.setProperty(name, value);
        record.ruler.textContent = prefix;
        width = record.ruler.getBoundingClientRect().width;
        record.prefixWidths.set(key, width);
      }
    }
    this.setPropertyFor(record, input, '--para-phone-prefix-width', `${width}px`);
    this.setAttributeFor(record, input, 'data-para-phone-prefix', String(prefix.length));
    const empty = prefix && !input.value.slice(prefix.length).trim();
    const hint = empty && this.dataset.labelStyle === 'placeholder'
      ? input.getAttribute('placeholder') || input.getAttribute('aria-label') || '' : '';
    this.setAttributeFor(record, input.parentElement, 'data-para-phone-hint', hint);
    // Keep the caret in the visible number without changing selections used for copy/paste.
    if (prefix && record.host.shadowRoot.activeElement === input
      && input.selectionStart === input.selectionEnd && input.selectionStart < prefix.length) {
      input.setSelectionRange(prefix.length, prefix.length);
    }
    if (input.scrollLeft < 0) input.scrollLeft = 0;
  }

  release(record) {
    record.observer.disconnect();
    record.events.abort();
    for (const timer of record.countryTimers) clearTimeout(timer);
    cancelAnimationFrame(record.frame);
    record.ruler?.remove();
    record.style.remove();
    for (const [element, saved] of record.properties) {
      for (const [name, value] of saved) {
        if (element.style.getPropertyValue(name) === value.applied) {
          if (value.before) element.style.setProperty(name, value.before, value.priority);
          else element.style.removeProperty(name);
        }
      }
    }
    for (const [element, saved] of record.attributes) {
      for (const [name, value] of saved) {
        if (element.getAttribute(name) === value.applied) {
          if (value.before === null) element.removeAttribute(name);
          else element.setAttribute(name, value.before);
        }
      }
    }
    for (const [input, saved] of record.placeholders) {
      if (input.getAttribute('placeholder') === saved.applied) {
        if (saved.before === null) input.removeAttribute('placeholder');
        else input.setAttribute('placeholder', saved.before);
      }
    }
    if (OWNERS.get(record.root) === this) OWNERS.delete(record.root);
  }
}

if (!customElements.get('para-shopify-form-style')) customElements.define('para-shopify-form-style', ParaShopifyFormStyle);
